import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '../../contexts/UserStoreContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';

const InvoicesList = () => {
    const navigate = useNavigate();
    const { invoices, loading, error, fetchInvoices } = useUserStore();
    const [filterStatus, setFilterStatus] = useState('all');
    const [sortBy, setSortBy] = useState('date');

    useEffect(() => {
        fetchInvoices();
    }, [fetchInvoices]);

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'paid':
            case 'succeeded':
                return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400';
            case 'pending':
            case 'sent':
                return 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400';
            case 'active':
                return 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400';
            case 'overdue':
            case 'failed':
                return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400';
            case 'draft':
                return 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-900/30 dark:text-gray-400';
            case 'canceled':
                return 'bg-muted/10 text-muted-foreground border-muted/20';
            default:
                return 'bg-muted text-muted-foreground border-border';
        }
    };

    const canPayInvoice = (invoice) => {
        const status = (invoice?.status || '').toLowerCase();
        return status !== 'paid' && status !== 'canceled';
    };

    const filteredInvoices = invoices
        .filter(inv => filterStatus === 'all' || inv.status === filterStatus)
        .sort((a, b) => {
            if (sortBy === 'date') return new Date(b.issue_date || b.date) - new Date(a.issue_date || a.date);
            if (sortBy === 'amount') return b.amount - a.amount;
            if (sortBy === 'status') return a.status.localeCompare(b.status);
            return 0;
        });

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <Header />
            <div className="flex-1 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-5xl mx-auto">
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-foreground mb-2">Invoices</h1>
                            <p className="text-muted-foreground">Manage your billing history and payments.</p>
                        </div>
                        <div className="flex items-center space-x-3">
                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                className="bg-card border border-border rounded-lg text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20"
                            >
                                <option value="all">All Status</option>
                                <option value="draft">Draft</option>
                                <option value="sent">Sent</option>
                                <option value="active">Active</option>
                                <option value="paid">Paid</option>
                                <option value="overdue">Overdue</option>
                                <option value="canceled">Canceled</option>
                            </select>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="bg-card border border-border rounded-lg text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20"
                            >
                                <option value="date">Sort by Date</option>
                                <option value="amount">Sort by Amount</option>
                                <option value="status">Sort by Status</option>
                            </select>
                        </div>
                    </div>

                    {loading && invoices.length === 0 ? (
                        <div className="flex items-center justify-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                        </div>
                    ) : error ? (
                        <div className="bg-destructive/10 text-destructive p-6 rounded-xl text-center">
                            <Icon name="AlertCircle" size={48} className="mx-auto mb-4" />
                            <h2 className="text-xl font-bold mb-2">Failed to load invoices</h2>
                            <p className="mb-6">{error}</p>
                            <Button onClick={() => fetchInvoices()}>Try Again</Button>
                        </div>
                    ) : filteredInvoices.length === 0 ? (
                        <div className="bg-card border border-border rounded-2xl p-12 text-center">
                            <Icon name="FileText" size={64} className="mx-auto mb-4 text-muted-foreground/30" />
                            <h2 className="text-xl font-bold text-foreground mb-2">No invoices found</h2>
                            <p className="text-muted-foreground">You don't have any invoices matching your filters.</p>
                        </div>
                    ) : (
                        <div className="grid gap-4">
                            {filteredInvoices.map((invoice) => (
                                <div
                                    key={invoice.id}
                                    className="bg-card border border-border rounded-xl p-6 hover:shadow-elevation-2 transition-all duration-200 cursor-pointer group"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-4">
                                            <div className="p-3 bg-primary/10 rounded-xl group-hover:bg-primary/20 transition-colors">
                                                <Icon name="FileText" size={24} className="text-primary" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-foreground">
                                                    #{invoice.invoice_number || invoice.id.substring(0, 8)}
                                                </h3>
                                                <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                                                    <span className="flex items-center">
                                                        <Icon name="Calendar" size={14} className="mr-1" />
                                                        {new Date(invoice.issue_date || invoice.date).toLocaleDateString()}
                                                    </span>
                                                    {invoice.description && (
                                                        <span className="truncate max-w-[200px] hidden sm:inline">
                                                            • {invoice.description}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-6">
                                            <div className="text-right">
                                                <p className="text-xl font-black text-foreground">
                                                    {invoice.currency || '$'} {(invoice.amount / 100).toFixed(2)}
                                                </p>
                                                <div className={`mt-1 px-2.5 py-0.5 rounded-full text-xs font-bold border inline-block ${getStatusColor(invoice.status)}`}>
                                                    {invoice.status.toUpperCase()}
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-3">
                                                {canPayInvoice(invoice) && (
                                                    <Button
                                                        variant="default"
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/invoices/${invoice.id}`);
                                                        }}
                                                        className="h-8"
                                                    >
                                                        <Icon name="CreditCard" size={14} className="mr-1" />
                                                        Pay
                                                    </Button>
                                                )}
                                                <Icon name="ChevronRight" size={20} className="text-muted-foreground group-hover:text-foreground transition-colors" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InvoicesList;
